import React, { Component } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import Link from 'redux-first-router-link';
import CookiesNotification from './CookiesNotification';
import Notification from './Notification';
import Navigation from './Navigation';
import Search from './Search';
import Cart from '../Selection/Cart';
import Select from '../Elements/Select/Select';
import { translate, prefix } from '../../utils/locale';
import { addEvent, removeEvent, hasClass, addClass, removeClass, offset } from '../../utils/dom';
import { removeNotification, clearFilter, changeLanguage, goToPage, changeDevice, cookieConsent } from '../../actions/environment';
import { clientInit, changeCountry, changeAddressInput } from '../../actions/checkout';
import s from './Header.scss';

let stickyOffset = 46; // tablet, desktop 52
const breakPoints = [900, 600];
const media = { w_600: 'mobile', w_900: 'tablet', w_1200: 'desktop' };

class Header extends Component {
	constructor(props) {
		super(props);

		this.state = {
			location: false,
			grid: false,
			menuPopupHeight: 'auto',
			menuOffset: null,
			cartOpen: false,
			favouritesOpen: false,
		};

		this.headerElement = React.createRef();
		this.linksElement = React.createRef();
		this.cartElement = React.createRef();
		this.languageCountryElement = React.createRef();
	}

	componentDidMount() {
		if (typeof window !== 'undefined') {
			const $html = document.querySelector('html');
			let currentWidth = breakPoints.reduce((acc, bp) => (bp > window.innerWidth ? bp : acc), 1200);
			let device = media[`w_${currentWidth}`];

			this.props.clientInit();
			this.props.changeDevice(device);
			if (device === 'tablet' || device === 'desktop') stickyOffset = 70;
			else stickyOffset = 46;

			// Wait for page to be rendered with all relevant elements.
			const initiateSiteListeners = () => {
				if (!this.headerElement || !this.linksElement || !this.cartElement) {
					setTimeout(initiateSiteListeners, 200);
				}

				const menuOffset = offset(this.headerElement.current).top + stickyOffset;

				let isScrolled = false;
				let headerIsFixed = false;
				let isScrollingUp = false;
				let lastScrolledDown = 0;
				let lastScrolledUp = 1e5;

				// initial hiding of search field
				this.setState({ showSearch: false });

				if (this.state.menuOffset === null) {
					this.setState({
						menuOffset,
						menuPopupHeight: window.innerHeight - 50,
					});
				}

				const resizeEvent = () => {
					const updatedWidth = breakPoints.reduce((acc, bp) => (bp > window.innerWidth ? bp : acc), 1200);
					this.setState({
						menuOffset: offset(this.headerElement.current).top + stickyOffset,
						menuPopupHeight: window.innerHeight - 50,
					});

					// Switch between mobile, table and desktop.
					if (currentWidth !== updatedWidth) {
						device = media[`w_${updatedWidth}`];
						this.props.changeDevice(device);
						if (device === 'tablet' || device === 'desktop') stickyOffset = 70;
						else stickyOffset = 46;
						currentWidth = updatedWidth;
					}
				};

				addEvent(window, 'resize.logoHeader', () => {
					window.requestAnimationFrame(resizeEvent);
				});

				const scrollEvent = () => {
					const top = window.pageYOffset || document.documentElement.scrollTop;
					if (this.state.menuOffset === null) {
						this.setState({
							menuOffset: offset(this.headerElement.current).top + stickyOffset,
							menuPopupHeight: window.innerHeight - 50,
						});
					}

					// make logo sticky when level with header.
					if (!headerIsFixed && top >= this.state.menuOffset) {
						headerIsFixed = true;
						addClass($html, 'u_headerIsFixed');
						this.setState({ location: false });
					} else if (headerIsFixed && top < this.state.menuOffset - stickyOffset) {
						headerIsFixed = false;
						removeClass($html, 'u_headerIsFixed');
					}

					if (top > 0 && !isScrolled) {
						addClass($html, 'u_pageIsScrolled');
						isScrolled = true;
					} else if (top === 0) {
						window.setTimeout(() => {
							removeClass($html, 'u_pageIsScrolled');
							removeClass($html, 'u_headerIsFixed');
							removeClass($html, 'u_pageIsScrollingUp');
						}, 2000);
						isScrolled = false;
						headerIsFixed = false;
					}

					if (top > lastScrolledDown) lastScrolledDown = top;
					if ((top < lastScrolledUp && isScrollingUp) || !isScrollingUp) lastScrolledUp = top;

					// console.log('DEBUG: lastScrolledDown, lastScrolledUp, top', lastScrolledDown, lastScrolledUp, top, isScrollingUp);

					if (lastScrolledDown - top > 50 && !isScrollingUp) {
						addClass($html, 'u_pageIsScrollingUp');
						isScrollingUp = true;
					}
					if (top - lastScrolledUp > 50 && isScrollingUp) {
						removeClass($html, 'u_pageIsScrollingUp');
						this.setState({ location: false });
						lastScrolledDown = top;
						isScrollingUp = false;
					}
				};

				addEvent(window, 'scroll.logoHeader', () => {
					window.requestAnimationFrame(scrollEvent);
				});
			};

			initiateSiteListeners();

			if ('ontouchstart' in document.documentElement) {
				removeClass($html, 'no-touchevents');
				addClass($html, 'touchevents');
			}

			if (hasClass($html, 'no-touchevents')) {
				addEvent(window, 'touchstart.touchTest', () => {
					removeClass($html, 'no-touchevents');
					addClass($html, 'touchevents');
					removeEvent(window, 'touchstart.touchTest');
				});
			}

			addEvent(document.querySelector('body'), 'click.linkCapture', (e) => {
				const $link = e.target.closest('a');

				if ($link && this.props.checkout.klarnaUnloaded && $link.getAttribute('href').indexOf('#') !== 0) {
					e.preventDefault();
					e.stopPropagation();
					window.location = $link.href;
					// Force full page reload to clear Klarna on next page click.
				} else if ($link && hasClass($link, 'linkCapture')) {
					e.preventDefault();
					this.props.goToPage($link.getAttribute('href'));
				}
			});

			addEvent(window, 'keydown.general', (e) => {
				if (e.key === 'Escape') {
					if (this.state.cartOpen) this.setState({ cartOpen: false });
					if (this.state.favouritesOpen) this.setState({ favouritesOpen: false });
					if (this.state.location === true) this.setState({ location: false });
					if (this.state.grid) this.setState({ grid: false });
				} else if (e.shiftKey && e.ctrlKey && e.key === 'A') {
					// ctrl + shift + a = Toggle grid
					this.setState((prevState) => ({ grid: !prevState.grid }));
				}
			});
		}
	}

	componentWillUnmount() {
		removeEvent(document.querySelector('body'), 'click.cartClose');
		removeEvent(window, 'keydown.general');
		removeEvent(window, 'scroll.logoHeader');
		removeEvent(window, 'resize.logoHeader');
		removeEvent(window, 'message.acuityListener');
	}

	openLocation = (e) => {
		e.preventDefault();

		this.setState((prevState) => ({ location: !prevState.location }));
	}

	changeLanguage = (e) => {
		const changeTo = e.currentTarget.dataset.language;
		this.props.changeLanguage(changeTo);
		this.setState({
			location: false,
		});
	}

	toggleCartHandler = (e) => {
		if (e && typeof e !== 'boolean') e.preventDefault();

		if (hasClass(document.body, 'u_addedToBag')) {
			removeClass(document.body, 'u_addedToBag');
		} else if (typeof e === 'boolean') {
			this.setState({ cartOpen: e });
		} else if (this.state.cartOpen) {
			this.setState({ cartOpen: false });
		} else if (!this.state.cartOpen) {
			this.setState({
				menuPopupHeight: window.innerHeight - 50,
				cartOpen: true,
				favouritesOpen: false,
			});
		} else if (this.props.checkout.klarnaUnloaded) {
			window.location = `${window.location.origin}/${this.props.setup.language}/selection`;
		}
	}

	toggleFavouritesHandler = (e) => {
		if (e) e.preventDefault();

		if (this.state.favouritesOpen) {
			this.setState({ favouritesOpen: false });
		} else if (!this.state.favouritesOpen) {
			this.setState({
				menuPopupHeight: window.innerHeight - 50,
				cartOpen: false,
				favouritesOpen: true,
			});
		}
	}

	toggleSearchHandler = (e) => {
		const { setup, location } = this.props;
		if (e) e.preventDefault();

		this.setState((prevState) => {
			if (location.type === 'SEARCH') {
				let searchReturn = prefix(setup, '');
				if (location.prev && location.prev.type !== 'SEARCH' && location.prev.pathname) searchReturn = location.prev.pathname;

				if (prevState.showSearch) this.props.goToPage(searchReturn.replace(prefix(setup, ''), ''));
			}
			return { showSearch: !prevState.showSearch };
		});
	}

	closeSearchHandler = () => {
		const { setup, location } = this.props;

		this.setState({ showSearch: false });

		if (location.type === 'SEARCH') {
			let searchReturn = prefix(setup, '');
			if (location.prev && location.prev.type !== 'SEARCH' && location.prev.pathname) searchReturn = location.prev.pathname;

			this.props.goToPage(searchReturn.replace(prefix(setup, ''), ''));
		}
	}

	notificationRemovedHandler = () => {
		setTimeout(() => {
			const menuOffset = offset(this.headerElement.current).top + stickyOffset;

			this.setState({
				menuOffset,
			});
		}, 500);
	}

	resetFilterHandler = () => {
		this.props.resetFilter();
	}

	render() {
		const { properties, navigation, setup, header, favourites, checkout, loggedIn, location } = this.props;
		const { grid, menuPopupHeight, cartOpen, favouritesOpen } = this.state;
		const { totals = {} } = checkout.selection;

		if (properties.siteInitiated === false || properties.siteClosed === true) return null;

		const countries = Object.keys(setup.countries).filter((country) => setup.countries[country].shipTo).reduce((acc, country) => (country !== '0' ? [...acc, { key: country, value: setup.countries[country].name }] : [...acc]), []);
		const onCountryChange = (id, value) => {
			this.props.changeCountry(value);
			this.props.changeAddressInput('address', 'country', value);
			this.props.changeAddressInput('shippingAddress', 'country', value);
		};

		const headerShowingMobile = /*!location?.prev?.type ||*/ location?.type === 'START';
		let loggedInText = translate(setup, 'headerSignIn');
		if (loggedIn && location.type !== 'ACCOUNT') loggedInText = translate(setup, 'headerView');
		if (loggedIn && location.type === 'ACCOUNT') loggedInText = translate(setup, 'headerSignOut');

		return (
			<>
				{ setup.unableToSendToCountry && <Notification type="error" content={[{ name: 'unableToSendToCountry', text: setup.statics.unableToSendToCountry }]} setup={setup} /> }
				<div className={s.notifications}>
					<CookiesNotification type="cookies" content={header.cookies} setup={setup} cookieConsent={this.props.cookieConsent} />
					<Notification type="popup" content={header.popup} setup={setup} notificationRemoved={this.notificationRemovedHandler} removeNotification={this.props.removeNotification} />
				</div>
				<header className={[s.header, (location.type === 'SELECTION' ? s.header__selection : null), (cartOpen ? s.header__cartOpen : null)].join(' ')} ref={this.headerElement}>
					<Link className={[s.header_logo, (headerShowingMobile ? s.header_logo__showing : null)].join(' ')} to={prefix(setup)} onClick={this.closeSearchHandler}>
						<svg xmlns="http://www.w3.org/2000/svg" width="125" height="13" viewBox="0 0 125 13">
							<g transform="translate(1 1)">
								<path d="M8.97,0.24 L8.97,1.28 L6.96,1.28 L6.96,9.26 L6.88,9.39 C6.04,10.72 4.91,11.39 3.48,11.39 C3.13,11.39 2.79,11.36 2.47,11.3 C2.15,11.24 1.86,11.14 1.58,11.01 C0.95,10.73 0.42,10.31 8.8817842e-16,9.74 L8.8817842e-16,7.25 L1.04,7.25 L1.04,9.36 C1.41,9.79 1.86,10.08 2.41,10.23 C2.64,10.29 2.85,10.33 3.04,10.35 C3.23,10.36 3.38,10.37 3.48,10.37 C3.57,10.37 3.7,10.37 3.88,10.35 C4.06,10.34 4.26,10.29 4.48,10.2 C4.7,10.11 4.94,9.97 5.18,9.78 C5.43,9.59 5.67,9.32 5.91,8.96 L5.91,1.28 L2.81,1.28 L2.81,0.24 L8.97,0.24 Z"/><polygon points="22.76 7.36 22.76 11.08 14.55 11.08 14.55 10.04 15.84 10.04 15.84 1.28 14.55 1.28 14.55 .24 22.43 .24 22.43 3.6 21.39 3.6 21.39 1.28 16.89 1.28 16.89 4.95 19 4.95 19 3.58 20.04 3.58 20.04 7.36 19 7.36 19 5.99 16.89 5.99 16.89 10.04 21.72 10.04 21.72 7.36 22.76 7.36"/><path d="M38.04,10.04 L38.04,11.08 L34.3,11.08 L34.3,10.04 L35.8,10.04 L34.95,7.57 L30.73,7.57 L29.89,10.04 L31.39,10.04 L31.39,11.08 L27.65,11.08 L27.65,10.04 L28.85,10.04 L31.78,1.28 L29.94,1.28 L29.94,0.24 L33.53,0.24 L36.85,10.04 L38.05,10.04 L38.04,10.04 L38.04,10.04 Z M34.58,6.52 L32.81,1.31 L31.06,6.52 L34.58,6.52 Z"/><polygon points="51.81 .24 51.81 1.28 50.52 1.28 50.52 11.08 49.34 11.08 44.51 2.04 44.51 10.04 46.26 10.04 46.26 11.08 42.19 11.08 42.19 10.04 43.46 10.04 43.46 1.28 42.19 1.28 42.19 .24 44.74 .24 49.47 9.19 49.47 1.29 47.95 1.29 47.95 .25 51.81 .25"/><polygon points="65.28 7.36 65.28 11.08 57.07 11.08 57.07 10.04 58.36 10.04 58.36 1.28 57.07 1.28 57.07 .24 64.95 .24 64.95 3.6 63.91 3.6 63.91 1.28 59.41 1.28 59.41 4.95 61.52 4.95 61.52 3.58 62.56 3.58 62.56 7.36 61.52 7.36 61.52 5.99 59.41 5.99 59.41 10.04 64.24 10.04 64.24 7.36 65.28 7.36"/><path d="M79.99,10.04 L79.99,11.08 L78.17,11.08 L74.98,6.37 L73.08,6.37 L73.08,10.04 L74.75,10.04 L74.75,11.08 L70.7,11.08 L70.7,10.04 L72.03,10.04 L72.03,1.28 L70.7,1.28 L70.7,0.24 L75.3,0.24 C75.44,0.24 75.64,0.24 75.9,0.27 C76.16,0.29 76.45,0.36 76.77,0.49 C77.09,0.62 77.44,0.82 77.8,1.11 C78.16,1.4 78.51,1.8 78.84,2.33 L78.84,4.27 C78.46,4.88 78.05,5.34 77.6,5.66 C77.15,5.98 76.66,6.19 76.12,6.31 L78.8,10.05 L80,10.05 L79.99,10.05 L79.99,10.05 L79.99,10.04 Z M77.79,2.67 C77.56,2.33 77.33,2.06 77.08,1.87 C76.83,1.68 76.6,1.54 76.37,1.45 C76.14,1.36 75.93,1.31 75.74,1.3 C75.55,1.29 75.4,1.28 75.28,1.28 L73.08,1.28 L73.08,5.33 L75.28,5.33 C75.39,5.33 75.55,5.33 75.75,5.31 C75.95,5.3 76.16,5.25 76.39,5.16 C76.92,4.95 77.39,4.53 77.8,3.93 L77.8,2.67 C77.8,2.67 77.79,2.67 77.79,2.67 L77.79,2.67 Z"/><polygon points="90.04 1.28 90.04 10.04 92.62 10.04 92.62 11.08 86.43 11.08 86.43 10.04 88.99 10.04 88.99 1.28 86.43 1.28 86.43 .24 92.62 .24 92.62 1.28"/><path d="M107.86 8.9C107.49 9.52 107.1 10 106.68 10.34 106.26 10.68 105.86 10.92 105.47 11.08 105.08 11.24 104.73 11.33 104.42 11.36 104.1 11.39 103.86 11.4 103.7 11.4 103.48 11.4 103.21 11.36 102.87 11.28 102.53 11.2 102.18 11.05 101.8 10.82 101.42 10.6 101.03 10.3 100.63 9.93 100.23 9.56 99.86 9.08 99.52 8.49L99.52 2.51 99.6 2.36C99.98 1.78 100.38 1.32 100.79 1 101.2.68 101.6.44 101.98.3 102.36.15 102.7.07 103.01.04 103.31.01 103.55 0 103.71 0 104.12 0 104.57.07 105.07.21 105.57.35 106.03.59 106.45.93L106.45.23 107.49.23 107.49 3.38 106.45 3.38 106.45 3.27C106.45 2.37 106.01 1.73 105.12 1.35 104.94 1.27 104.77 1.22 104.61 1.18 104.44 1.14 104.29 1.11 104.15 1.08 104 1.05 103.85 1.04 103.71 1.04 103.57 1.04 103.38 1.04 103.15 1.07 102.92 1.09 102.65 1.16 102.36 1.27 101.66 1.54 101.07 2.04 100.57 2.79L100.57 8.19C100.92 8.72 101.28 9.14 101.64 9.43 102 9.73 102.33 9.94 102.63 10.08 102.93 10.21 103.17 10.29 103.37 10.32 103.57 10.35 103.68 10.36 103.7 10.36 103.84 10.36 104.03 10.36 104.27 10.33 104.51 10.31 104.78 10.24 105.09 10.12 105.39 10 105.71 9.8 106.04 9.53 106.37 9.26 106.69 8.87 106.99 8.36L107.86 8.91 107.86 8.91 107.86 8.9ZM123.08 10.04 123.08 11.08 119.34 11.08 119.34 10.04 120.84 10.04 119.99 7.57 115.77 7.57 114.93 10.04 116.43 10.04 116.43 11.08 112.69 11.08 112.69 10.04 113.89 10.04 116.82 1.28 114.98 1.28 114.98.24 118.57.24 121.89 10.04 123.09 10.04 123.08 10.04 123.08 10.04ZM119.62 6.52 117.85 1.31 116.1 6.52 119.62 6.52Z" />
							</g>
						</svg>
					</Link>
					<Link className={s.header_back} to={prefix(setup)}>
						{ translate(setup, 'headerCheckoutBack') }
					</Link>
					{
						location.type !== 'SELECTION' && (
							<div className={s.header_fixedContainer}>
								<div className={s.header_links} ref={this.linksElement}>
									<Navigation navigation={navigation} setup={setup} menuPopupHeight={menuPopupHeight} loggedIn={loggedIn} closeSearch={this.closeSearchHandler} />
									<div className={s.functions}>
										<div className={s.functions_location} style={{ width: `${setup?.countries?.[setup.country]?.name ? (Math.ceil(setup.countries[setup.country].name.length * 10) + 8) : 65}px` }}>
											<Select variantClasses={['label__country']} id="country" selected={setup.country} options={countries} inputChange={onCountryChange} label={`${translate(setup, 'labelShippingTo')}: `} />
										</div>

										<Link className={s.functions_account} to={loggedIn && location.type === 'ACCOUNT' ? prefix(setup, 'logout') : prefix(setup, 'account')}>
											<span>{ translate(setup, 'headerAccount') }: </span>
											<span>{ loggedInText }</span>
										</Link>

										<a className={s.functions_search} onClick={this.toggleSearchHandler} href="#toggle-search">
											<svg width="14" height="16" viewBox="0 0 14 16" fill="none" xmlns="http://www.w3.org/2000/svg">
												<line x1="7.37963" y1="8.6746" x2="13.3796" y2="15.6746" stroke="black" />
												<circle cx="5" cy="5" r="4.5" stroke="black" />
											</svg>
										</a>

										<Link className={s.functions_favourites} to={prefix(setup, 'favourites')} onClick={this.toggleFavouritesHandler}>
											<svg className={s.functions_favourites_icon} width="14" height="22" viewBox="0 0 14 22" fill="none" xmlns="http://www.w3.org/2000/svg">
												<path d={`M0 0V22L7 15.1075L14 22V0H0Z${favourites.length > 0 ? '' : 'M12.8649 19.3016L7 13.5269L1.13513 19.3016V1.11769H12.8648L12.8649 19.3016Z'}`} fill="black" />
											</svg>
										</Link>

										<Link className={s.functions_selection} to={prefix(setup, 'selection')} onClick={this.toggleCartHandler}>
											<span className={s.functions_selection_icon}>{ totals.totalQuantity || 0 }</span>
										</Link>

										<div className={[s.cart, cartOpen ? s.cart__open : null].join(' ')} ref={this.cartElement}>
											<button className={s.cart_screen} type="button" aria-label="Close Cart" onClick={this.toggleCartHandler} />
											<div className={s.cart_inner}>
												<Cart maxHeight={menuPopupHeight} toggleCartHandler={this.toggleCartHandler} />
											</div>
										</div>
										<div className={[s.favourites, favouritesOpen ? s.favourites__open : null].join(' ')} ref={this.favouritesElement}>
											<button className={s.favourites_screen} type="button" aria-label="Close Favourites" onClick={this.toggleFavouritesHandler} />
											<div className={s.favourites_inner}>
												<Cart maxHeight={menuPopupHeight} toggleCartHandler={this.toggleFavouritesHandler} favourites />
											</div>
										</div>
									</div>
								</div>
							</div>
						)
					}
				</header>
				<Notification type="campaign" content={header.campaign} headerShowingMobile={headerShowingMobile} setup={setup} notificationRemoved={this.notificationRemovedHandler} removeNotification={this.props.removeNotification} />
				{
					(this.state.showSearch || location.type === 'SEARCH') && (
						<div className={[s.search, (location.type === 'SEARCH' ? s.search__active : null)].join(' ')}>
							<div className={s.searchScreen} />
							<Search closeSearch={this.closeSearchHandler} />
						</div>
					)
				}
				{ grid && <div className={s.grid}><div /></div> }
			</>
		);
	}
}

const mapState = ({ environment, checkout, location }) => {
	const { properties, navigation, setup, header, pageType, favourites } = environment;
	return { properties, navigation, setup, header, pageType, favourites, checkout, loggedIn: checkout.account.loggedIn, location };
};

export default hot(module)(connect(mapState, {
	removeNotification,
	clearFilter,
	changeCountry,
	changeAddressInput,
	changeLanguage,
	goToPage,
	clientInit,
	changeDevice,
	cookieConsent,
})(Header));
